import React, { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { 
  XMarkIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline'

import { jobsApi, resumesApi, interviewsApi } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'

interface Job {
  id: string
  title: string
  department?: string
  location?: string
  status: string
}

interface Resume {
  id: string
  original_filename: string
  candidate_name?: string
  status: string
  ai_analysis?: any
}

interface CreateInterviewModalProps {
  isOpen: boolean
  onClose: () => void
  onInterviewCreated: () => void
}

const CreateInterviewModal: React.FC<CreateInterviewModalProps> = ({
  isOpen,
  onClose,
  onInterviewCreated
}) => {
  const [selectedJobId, setSelectedJobId] = useState<string>('')
  const [selectedResumeId, setSelectedResumeId] = useState<string>('')
  const [interviewType, setInterviewType] = useState<string>('technical')
  const [maxQuestions, setMaxQuestions] = useState<number>(15)
  const [targetDuration, setTargetDuration] = useState<number>(45)
  const [confidenceThreshold, setConfidenceThreshold] = useState<number>(0.8)
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false)

  // Fetch jobs
  const { data: jobs, isLoading: jobsLoading } = useQuery<Job[]>({
    queryKey: ['jobs-for-interview'],
    queryFn: async () => {
      const response = await jobsApi.list({ status: 'active' })
      return response.data.jobs || []
    },
    enabled: isOpen
  })

  // Fetch resumes
  const { data: resumes, isLoading: resumesLoading } = useQuery<Resume[]>({
    queryKey: ['resumes-for-interview'],
    queryFn: async () => {
      const response = await resumesApi.list({ status: 'analyzed' })
      return response.data.resumes || []
    },
    enabled: isOpen
  })

  // Create interview mutation
  const createInterviewMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await interviewsApi.createSession(data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Interview session created successfully!')
      onInterviewCreated()
      handleClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create interview session')
    }
  })

  const handleClose = () => {
    setSelectedJobId('')
    setSelectedResumeId('')
    setInterviewType('technical')
    setMaxQuestions(15)
    setTargetDuration(45)
    setConfidenceThreshold(0.8)
    setShowAdvanced(false)
    onClose()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedJobId || !selectedResumeId) {
      toast.error('Please select both a job and a resume')
      return
    }

    const createData = {
      job_id: selectedJobId,
      resume_id: selectedResumeId,
      interview_type: interviewType,
      max_questions: maxQuestions,
      target_duration_minutes: targetDuration,
      confidence_threshold: confidenceThreshold
    }

    createInterviewMutation.mutate(createData)
  }

  const selectedJob = jobs?.find(job => job.id === selectedJobId)
  const selectedResume = resumes?.find(resume => resume.id === selectedResumeId)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Create Virtual Interview</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Job Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <BriefcaseIcon className="w-4 h-4 inline mr-1" />
              Select Job Position *
            </label>
            {jobsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <select
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a job position...</option>
                {jobs?.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.title} {job.department && `- ${job.department}`} {job.location && `(${job.location})`}
                  </option>
                ))}
              </select>
            )}
            {selectedJob && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm font-medium text-blue-900">
                  {selectedJob.title}
                </div>
                {selectedJob.department && (
                  <div className="text-sm text-blue-700">
                    Department: {selectedJob.department}
                  </div>
                )}
                {selectedJob.location && (
                  <div className="text-sm text-blue-700">
                    Location: {selectedJob.location}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Resume Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DocumentTextIcon className="w-4 h-4 inline mr-1" />
              Select Candidate Resume *
            </label>
            {resumesLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <select
                value={selectedResumeId}
                onChange={(e) => setSelectedResumeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a candidate resume...</option>
                {resumes?.map((resume) => (
                  <option key={resume.id} value={resume.id}>
                    {resume.candidate_name || resume.original_filename}
                  </option>
                ))}
              </select>
            )}
            {selectedResume && (
              <div className="mt-2 p-3 bg-green-50 rounded-lg">
                <div className="text-sm font-medium text-green-900">
                  {selectedResume.candidate_name || selectedResume.original_filename}
                </div>
                <div className="text-sm text-green-700">
                  Status: {selectedResume.status}
                </div>
                {selectedResume.ai_analysis?.professional_summary && (
                  <div className="text-sm text-green-700 mt-1">
                    {selectedResume.ai_analysis.professional_summary.slice(0, 150)}...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Interview Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interview Type
            </label>
            <select
              value={interviewType}
              onChange={(e) => setInterviewType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="technical">Technical Interview</option>
              <option value="behavioral">Behavioral Interview</option>
              <option value="screening">Screening Interview</option>
              <option value="final">Final Interview</option>
            </select>
          </div>

          {/* Basic Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ClockIcon className="w-4 h-4 inline mr-1" />
                Target Duration (minutes)
              </label>
              <input
                type="number"
                value={targetDuration}
                onChange={(e) => setTargetDuration(parseInt(e.target.value) || 45)}
                min="15"
                max="120"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Questions
              </label>
              <input
                type="number"
                value={maxQuestions}
                onChange={(e) => setMaxQuestions(parseInt(e.target.value) || 15)}
                min="5"
                max="30"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Advanced Settings Toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center text-sm text-blue-600 hover:text-blue-700"
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4 mr-1" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
            </button>
          </div>

          {/* Advanced Settings */}
          {showAdvanced && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confidence Threshold (0.1 - 1.0)
                </label>
                <input
                  type="number"
                  value={confidenceThreshold}
                  onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value) || 0.8)}
                  min="0.1"
                  max="1.0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum confidence level required for skill assessment completion
                </p>
              </div>
            </div>
          )}

          {/* Interview Preview */}
          {selectedJobId && selectedResumeId && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Interview Preview</h3>
              <div className="space-y-1 text-sm text-blue-700">
                <div>🎯 <strong>Goal:</strong> Evaluate {selectedResume?.candidate_name || 'candidate'} for {selectedJob?.title}</div>
                <div>⏱️ <strong>Duration:</strong> Up to {targetDuration} minutes</div>
                <div>❓ <strong>Questions:</strong> Maximum {maxQuestions} adaptive questions</div>
                <div>🤖 <strong>AI Assessment:</strong> Real-time skill evaluation and scoring</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createInterviewMutation.isPending || !selectedJobId || !selectedResumeId}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {createInterviewMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Create Interview'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateInterviewModal